import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users,
  Zap,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  BarChart3,
  DollarSign,
  Activity,
  UserX,
  Trash2,
  Download,
  Shield,
  Car,
  Clock,
  Search,
  RefreshCw,
  LogOut,
  Star,
  XCircle,
  Eye,
  MoreVertical,
  FileText,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Home,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// ─── Types ────────────────────────────────────────────────────────────────────

interface User {
  id: string;
  full_name: string;
  phone: string;
  role: "driver" | "passenger" | "admin";
  suspended?: boolean;
  created_at: string;
  profile_photo?: string;
  rating?: number;
}

interface Driver extends User {
  is_premium: boolean;
  verified: boolean;
  rating: number;
  car_make?: string;
  car_model?: string;
  car_year?: string;
  plate_number?: string;
  seats?: number;
}

interface Trip {
  id: string;
  passenger_id: string;
  driver_id: string;
  status: "pending" | "accepted" | "in_progress" | "completed" | "cancelled";
  estimated_price: number;
  final_price?: number;
  pickup_location?: string;
  destination?: string;
  created_at: string;
  passenger?: { full_name: string; phone: string };
  driver?: { full_name: string; phone: string };
}

interface WaitlistEntry {
  id: string;
  name: string;
  phone: string;
  email?: string;
  area: string;
  role: "driver" | "passenger";
  created_at: string;
}

interface Verification {
  id: string;
  user_id: string;
  status: "pending" | "approved" | "rejected";
  id_type?: string;
  id_number?: string;
  front_image_url?: string;
  back_image_url?: string;
  selfie_url?: string;
  admin_notes?: string;
  created_at: string;
  updated_at?: string;
  profiles?: { full_name: string; phone: string; role: string };
}

interface Analytics {
  totalUsers: number;
  totalDrivers: number;
  verifiedDrivers: number;
  premiumDrivers: number;
  totalRides: number;
  completedRides: number;
  pendingRides: number;
  cancelledRides: number;
  totalRevenue: number;
  averageRidePrice: string | number;
  completionRate: string | number;
  last7Days: Array<{ date: string; revenue: number; rides: number }>;
}

interface RecentActivity {
  recentRides: Trip[];
  recentUsers: User[];
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

type TabId =
  | "overview"
  | "users"
  | "drivers"
  | "trips"
  | "verifications"
  | "waitlist"
  | "activity";

const NAV_ITEMS: { id: TabId; label: string; Icon: React.ElementType }[] = [
  { id: "overview", label: "Overview", Icon: Home },
  { id: "users", label: "Users", Icon: Users },
  { id: "drivers", label: "Drivers", Icon: Car },
  { id: "trips", label: "Trips", Icon: TrendingUp },
  { id: "verifications", label: "KYC / Verifications", Icon: Shield },
  { id: "waitlist", label: "Waitlist", Icon: Clock },
  { id: "activity", label: "Recent Activity", Icon: Activity },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COLORS = ["#10B981", "#FFD400", "#EF4444", "#0057FF"];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GH", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    accepted: "bg-blue-100 text-blue-800",
    in_progress: "bg-indigo-100 text-indigo-800",
    cancelled: "bg-red-100 text-red-800",
    active: "bg-green-100 text-green-800",
    suspended: "bg-red-100 text-red-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    driver: "bg-blue-100 text-blue-800",
    passenger: "bg-green-100 text-green-800",
    admin: "bg-purple-100 text-purple-800",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      {status}
    </span>
  );
}

function exportToCSV(data: Record<string, unknown>[], filename: string) {
  if (!data.length) return toast.error("No data to export");
  const headers = Object.keys(data[0]).join(",");
  const rows = data.map((row) =>
    Object.values(row)
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [headers, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({
  label,
  value,
  Icon,
  color,
  sub,
}: {
  label: string;
  value: string | number;
  Icon: React.ElementType;
  color: string;
  sub?: string;
}) {
  return (
    <Card className="p-5 hover:shadow-lg transition-all duration-200 bg-white border-0 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
            {label}
          </p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className="p-3 rounded-xl bg-gray-100">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </Card>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
      <FileText className="w-10 h-10 mb-3 opacity-40" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [users, setUsers] = useState<User[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tripPage, setTripPage] = useState(1);
  const [tripTotal, setTripTotal] = useState(0);
  const TRIPS_PER_PAGE = 20;

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [tripDetailLoading, setTripDetailLoading] = useState(false);

  useEffect(() => {
    if (!token) navigate("/auth/login");
  }, [token, navigate]);

  const apiFetch = useCallback(
    async (path: string, options: RequestInit = {}) => {
      const res = await fetch(path, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      });
      if (res.status === 401) {
        navigate("/auth/login");
        throw new Error("Unauthorized");
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Request failed");
      return data;
    },
    [token, navigate],
  );

  const fetchTab = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      switch (activeTab) {
        case "overview": {
          const data = await apiFetch("/api/admin/analytics/detailed");
          setAnalytics(data.analytics);
          break;
        }
        case "users": {
          const data = await apiFetch("/api/admin/users");
          setUsers(data.users || []);
          break;
        }
        case "drivers": {
          const data = await apiFetch("/api/admin/drivers");
          setDrivers(data.drivers || []);
          break;
        }
        case "trips": {
          const data = await apiFetch(
            `/api/admin/trips?page=${tripPage}&limit=${TRIPS_PER_PAGE}`,
          );
          setTrips(data.trips || []);
          setTripTotal(data.pagination?.total ?? 0);
          break;
        }
        case "verifications": {
          const data = await apiFetch("/api/admin/verifications");
          setVerifications(data.verifications || []);
          break;
        }
        case "waitlist": {
          const data = await apiFetch("/api/admin/waitlist");
          setWaitlist(data.waitlist || []);
          break;
        }
        case "activity": {
          const data = await apiFetch("/api/admin/recent-activity");
          setActivity({
            recentRides: data.recentRides,
            recentUsers: data.recentUsers,
          });
          break;
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      if (msg !== "Unauthorized") toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [activeTab, token, tripPage, apiFetch]);

  useEffect(() => {
    fetchTab();
  }, [fetchTab]);

  useEffect(() => {
    setTripPage(1);
    setSearchQuery("");
  }, [activeTab]);

  async function handleVerifyDriver(driverId: string) {
    try {
      await apiFetch("/api/admin/verify-driver", {
        method: "POST",
        body: JSON.stringify({ driver_id: driverId }),
      });
      toast.success("Driver verified");
      fetchTab();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to verify driver",
      );
    }
  }

  async function handleTogglePremium(driver: Driver) {
    try {
      await apiFetch(`/api/admin/drivers/${driver.id}/premium`, {
        method: "PATCH",
        body: JSON.stringify({ is_premium: !driver.is_premium }),
      });
      toast.success(
        driver.is_premium ? "Premium removed" : "Driver promoted to premium",
      );
      fetchTab();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update premium",
      );
    }
  }

  async function handleSuspendUser(userId: string, suspend: boolean) {
    try {
      await apiFetch("/api/admin/suspend-user", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, suspend }),
      });
      toast.success(suspend ? "User suspended" : "User activated");
      fetchTab();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    }
  }

  async function handleDeleteUser(userId: string, name: string) {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/delete-user/${userId}`, { method: "DELETE" });
      toast.success("User deleted");
      setSelectedUser(null);
      fetchTab();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user");
    }
  }

  async function handleReviewVerification(
    id: string,
    status: "approved" | "rejected",
    admin_notes?: string,
  ) {
    try {
      await apiFetch(`/api/admin/verifications/${id}/review`, {
        method: "POST",
        body: JSON.stringify({ status, admin_notes }),
      });
      toast.success(`Verification ${status}`);
      fetchTab();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to review verification",
      );
    }
  }

  async function openTripDetail(tripId: string) {
    setTripDetailLoading(true);
    try {
      const data = await apiFetch(`/api/admin/trips/${tripId}`);
      setSelectedTrip(data.trip);
    } catch {
      const local = trips.find((t) => t.id === tripId);
      if (local) setSelectedTrip(local);
    } finally {
      setTripDetailLoading(false);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/auth/login");
  }

  const q = searchQuery.toLowerCase();
  const filteredUsers = users.filter(
    (u) =>
      u.full_name?.toLowerCase().includes(q) || u.phone?.includes(searchQuery),
  );
  const filteredDrivers = drivers.filter(
    (d) =>
      d.full_name?.toLowerCase().includes(q) ||
      d.phone?.includes(searchQuery) ||
      d.plate_number?.toLowerCase().includes(q),
  );
  const filteredTrips = trips.filter(
    (t) =>
      t.id.includes(searchQuery) ||
      t.status.includes(q) ||
      t.passenger?.full_name?.toLowerCase().includes(q) ||
      t.driver?.full_name?.toLowerCase().includes(q),
  );
  const filteredWaitlist = waitlist.filter(
    (w) =>
      w.name?.toLowerCase().includes(q) ||
      w.phone?.includes(searchQuery) ||
      w.area?.toLowerCase().includes(q),
  );
  const filteredVerifications = verifications.filter(
    (v) =>
      v.profiles?.full_name?.toLowerCase().includes(q) || v.status.includes(q),
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? "w-60" : "w-16"} flex-shrink-0 bg-yenko-deep text-white flex flex-col transition-all duration-200 z-20`}
      >
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          <div className="w-8 h-8 bg-yenko-yellow rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-yenko-deep font-extrabold text-sm">Y</span>
          </div>
          {sidebarOpen && (
            <div>
              <p className="font-bold text-sm leading-none">Yenko</p>
              <p className="text-xs text-white/50 mt-0.5">Admin Console</p>
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV_ITEMS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all ${
                activeTab === id
                  ? "bg-yenko-blue text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </button>
          ))}
        </nav>

        {sidebarOpen && (
          <div className="px-4 py-3 border-t border-white/10">
            <p className="text-xs text-white/40 mb-0.5">Signed in as</p>
            <p className="text-xs font-medium text-white/80 truncate">
              {user?.full_name}
            </p>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? (
                <X className="w-4 h-4 text-gray-500" />
              ) : (
                <Menu className="w-4 h-4 text-gray-500" />
              )}
            </button>
            <h1 className="text-base font-bold text-yenko-deep">
              {NAV_ITEMS.find((n) => n.id === activeTab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTab}
              className="text-gray-500 hover:text-yenko-blue"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {activeTab === "overview" && (
            <OverviewTab analytics={analytics} loading={loading} />
          )}
          {activeTab === "users" && (
            <UsersTab
              users={filteredUsers}
              loading={loading}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onExport={() =>
                exportToCSV(
                  users as unknown as Record<string, unknown>[],
                  "users",
                )
              }
              onSuspend={handleSuspendUser}
              onDelete={handleDeleteUser}
              onView={setSelectedUser}
            />
          )}
          {activeTab === "drivers" && (
            <DriversTab
              drivers={filteredDrivers}
              loading={loading}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onExport={() =>
                exportToCSV(
                  drivers as unknown as Record<string, unknown>[],
                  "drivers",
                )
              }
              onVerify={handleVerifyDriver}
              onTogglePremium={handleTogglePremium}
              onSuspend={handleSuspendUser}
            />
          )}
          {activeTab === "trips" && (
            <TripsTab
              trips={filteredTrips}
              loading={loading}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onExport={() =>
                exportToCSV(
                  trips as unknown as Record<string, unknown>[],
                  "trips",
                )
              }
              onView={openTripDetail}
              page={tripPage}
              total={tripTotal}
              perPage={TRIPS_PER_PAGE}
              onPageChange={setTripPage}
            />
          )}
          {activeTab === "verifications" && (
            <VerificationsTab
              verifications={filteredVerifications}
              loading={loading}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onReview={handleReviewVerification}
            />
          )}
          {activeTab === "waitlist" && (
            <WaitlistTab
              waitlist={filteredWaitlist}
              loading={loading}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onExport={() =>
                exportToCSV(
                  waitlist as unknown as Record<string, unknown>[],
                  "waitlist",
                )
              }
            />
          )}
          {activeTab === "activity" && (
            <ActivityTab activity={activity} loading={loading} />
          )}
        </main>
      </div>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              Full profile for {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Full Name</p>
                  <p className="font-medium">{selectedUser.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Phone</p>
                  <p className="font-medium">{selectedUser.phone}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Role</p>
                  <StatusBadge status={selectedUser.role} />
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Status</p>
                  <StatusBadge
                    status={selectedUser.suspended ? "suspended" : "active"}
                  />
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Joined</p>
                  <p className="font-medium">
                    {fmtDate(selectedUser.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">User ID</p>
                  <p className="font-mono text-xs text-gray-500 truncate">
                    {selectedUser.id.slice(0, 16)}…
                  </p>
                </div>
              </div>
              {selectedUser.role !== "admin" && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleSuspendUser(
                        selectedUser.id,
                        !selectedUser.suspended,
                      );
                      setSelectedUser(null);
                    }}
                    className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50"
                  >
                    <UserX className="w-3 h-3 mr-1" />
                    {selectedUser.suspended ? "Activate" : "Suspend"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handleDeleteUser(selectedUser.id, selectedUser.full_name)
                    }
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Trip Detail Modal */}
      <Dialog
        open={!!selectedTrip || tripDetailLoading}
        onOpenChange={() => setSelectedTrip(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Trip Details</DialogTitle>
            <DialogDescription>
              Full information for ride {selectedTrip?.id?.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>
          {tripDetailLoading ? (
            <SkeletonRows rows={4} />
          ) : selectedTrip ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Status</p>
                  <StatusBadge status={selectedTrip.status} />
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Date</p>
                  <p className="font-medium">
                    {fmtDate(selectedTrip.created_at)}{" "}
                    {fmtTime(selectedTrip.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Est. Price</p>
                  <p className="font-medium">
                    ₵{Number(selectedTrip.estimated_price).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Final Price</p>
                  <p className="font-medium">
                    {selectedTrip.final_price
                      ? `₵${Number(selectedTrip.final_price).toFixed(2)}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Passenger</p>
                  <p className="font-medium">
                    {selectedTrip.passenger?.full_name ?? "Unknown"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {selectedTrip.passenger?.phone}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Driver</p>
                  <p className="font-medium">
                    {selectedTrip.driver?.full_name ?? "Unassigned"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {selectedTrip.driver?.phone}
                  </p>
                </div>
                {selectedTrip.pickup_location && (
                  <div className="col-span-2">
                    <p className="text-gray-400 text-xs mb-0.5">Pickup</p>
                    <p className="font-medium">
                      {selectedTrip.pickup_location}
                    </p>
                  </div>
                )}
                {selectedTrip.destination && (
                  <div className="col-span-2">
                    <p className="text-gray-400 text-xs mb-0.5">Destination</p>
                    <p className="font-medium">{selectedTrip.destination}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-gray-400 text-xs mb-0.5">Trip ID</p>
                  <p className="font-mono text-xs text-gray-500">
                    {selectedTrip.id}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function OverviewTab({
  analytics,
  loading,
}: {
  analytics: Analytics | null;
  loading: boolean;
}) {
  if (loading) return <SkeletonRows rows={8} />;
  if (!analytics) return <EmptyState text="No analytics data available" />;

  const pieData = [
    { name: "Completed", value: analytics.completedRides },
    { name: "Pending", value: analytics.pendingRides },
    { name: "Cancelled", value: analytics.cancelledRides },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Users"
          value={analytics.totalUsers}
          Icon={Users}
          color="text-blue-600"
          sub={`${analytics.totalDrivers} drivers`}
        />
        <StatCard
          label="Total Revenue"
          value={`₵${Number(analytics.totalRevenue).toFixed(0)}`}
          Icon={DollarSign}
          color="text-yellow-600"
          sub={`Avg ₵${analytics.averageRidePrice}`}
        />
        <StatCard
          label="Completed Trips"
          value={analytics.completedRides}
          Icon={CheckCircle}
          color="text-green-600"
          sub={`${analytics.completionRate}% rate`}
        />
        <StatCard
          label="Active Drivers"
          value={analytics.verifiedDrivers}
          Icon={Car}
          color="text-purple-600"
          sub={`${analytics.premiumDrivers} premium`}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-yenko-blue">
          <p className="text-xs text-gray-400 uppercase font-medium">
            Avg. Price
          </p>
          <p className="text-2xl font-bold mt-1">
            ₵{analytics.averageRidePrice}
          </p>
        </Card>
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-green-500">
          <p className="text-xs text-gray-400 uppercase font-medium">
            Completion
          </p>
          <p className="text-2xl font-bold mt-1">{analytics.completionRate}%</p>
        </Card>
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-yellow-400">
          <p className="text-xs text-gray-400 uppercase font-medium">
            Premium Drivers
          </p>
          <p className="text-2xl font-bold mt-1">{analytics.premiumDrivers}</p>
        </Card>
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-orange-500">
          <p className="text-xs text-gray-400 uppercase font-medium">
            Pending Rides
          </p>
          <p className="text-2xl font-bold mt-1">{analytics.pendingRides}</p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 bg-white shadow-sm border-0">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-yenko-blue" />
            <h3 className="font-semibold text-sm">Revenue — Last 7 Days</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={analytics.last7Days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) =>
                  new Date(v).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#0057FF"
                strokeWidth={2}
                dot={false}
                name="Revenue (₵)"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 bg-white shadow-sm border-0">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-yellow-500" />
            <h3 className="font-semibold text-sm">Rides — Last 7 Days</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={analytics.last7Days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) =>
                  new Date(v).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="rides"
                fill="#FFD400"
                name="Rides"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 bg-white shadow-sm border-0">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-purple-500" />
          <h3 className="font-semibold text-sm">Ride Status Distribution</h3>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-8">
          <ResponsiveContainer width={260} height={220}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                dataKey="value"
                paddingAngle={3}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-3 flex-1">
            {pieData.map((item, i) => (
              <div
                key={item.name}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ background: COLORS[i] }}
                  />
                  <span className="text-sm text-gray-600">{item.name}</span>
                </div>
                <span className="text-sm font-bold">{item.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-sm text-gray-500 font-medium">Total</span>
              <span className="text-sm font-bold">{analytics.totalRides}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab({
  users,
  loading,
  searchQuery,
  onSearch,
  onExport,
  onSuspend,
  onDelete,
  onView,
}: {
  users: User[];
  loading: boolean;
  searchQuery: string;
  onSearch: (q: string) => void;
  onExport: () => void;
  onSuspend: (id: string, suspend: boolean) => void;
  onDelete: (id: string, name: string) => void;
  onView: (u: User) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name or phone…"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
        <Badge variant="secondary">{users.length} users</Badge>
      </div>

      <Card className="bg-white shadow-sm border-0 overflow-hidden">
        {loading ? (
          <SkeletonRows />
        ) : users.length === 0 ? (
          <EmptyState text="No users found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {["Name", "Phone", "Role", "Status", "Joined", "Actions"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-gray-50/70 transition-colors group"
                  >
                    <td className="py-3 px-4 font-medium">{u.full_name}</td>
                    <td className="py-3 px-4 text-gray-500">{u.phone}</td>
                    <td className="py-3 px-4">
                      <StatusBadge status={u.role} />
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge
                        status={u.suspended ? "suspended" : "active"}
                      />
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-xs">
                      {fmtDate(u.created_at)}
                    </td>
                    <td className="py-3 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onView(u)}>
                            <Eye className="w-3 h-3 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {u.role !== "admin" && (
                            <>
                              <DropdownMenuItem
                                onClick={() => onSuspend(u.id, !u.suspended)}
                              >
                                <UserX className="w-3 h-3 mr-2" />
                                {u.suspended ? "Activate" : "Suspend"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => onDelete(u.id, u.full_name)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Drivers Tab ──────────────────────────────────────────────────────────────

function DriversTab({
  drivers,
  loading,
  searchQuery,
  onSearch,
  onExport,
  onVerify,
  onTogglePremium,
  onSuspend,
}: {
  drivers: Driver[];
  loading: boolean;
  searchQuery: string;
  onSearch: (q: string) => void;
  onExport: () => void;
  onVerify: (id: string) => void;
  onTogglePremium: (d: Driver) => void;
  onSuspend: (id: string, suspend: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search drivers…"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
        <Badge variant="secondary">{drivers.length} drivers</Badge>
      </div>

      <Card className="bg-white shadow-sm border-0 overflow-hidden">
        {loading ? (
          <SkeletonRows />
        ) : drivers.length === 0 ? (
          <EmptyState text="No drivers found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {[
                    "Driver",
                    "Phone",
                    "Rating",
                    "Verified",
                    "Premium",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {drivers.map((d) => (
                  <tr
                    key={d.id}
                    className="hover:bg-gray-50/70 transition-colors group"
                  >
                    <td className="py-3 px-4">
                      <p className="font-medium">{d.full_name}</p>
                      {d.plate_number && (
                        <p className="text-xs text-gray-400">
                          {d.plate_number}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500">{d.phone}</td>
                    <td className="py-3 px-4">
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        {Number(d.rating ?? 5).toFixed(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {d.verified ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-orange-400" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {d.is_premium ? (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                          Premium
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge
                        status={d.suspended ? "suspended" : "active"}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!d.verified && (
                            <DropdownMenuItem onClick={() => onVerify(d.id)}>
                              <CheckCircle className="w-3 h-3 mr-2 text-green-600" />
                              Verify Driver
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => onTogglePremium(d)}>
                            <Star className="w-3 h-3 mr-2 text-yellow-500" />
                            {d.is_premium ? "Remove Premium" : "Make Premium"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onSuspend(d.id, !d.suspended)}
                          >
                            <UserX className="w-3 h-3 mr-2" />
                            {d.suspended ? "Activate" : "Suspend"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Trips Tab ────────────────────────────────────────────────────────────────

function TripsTab({
  trips,
  loading,
  searchQuery,
  onSearch,
  onExport,
  onView,
  page,
  total,
  perPage,
  onPageChange,
}: {
  trips: Trip[];
  loading: boolean;
  searchQuery: string;
  onSearch: (q: string) => void;
  onExport: () => void;
  onView: (id: string) => void;
  page: number;
  total: number;
  perPage: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search trips…"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
        <Badge variant="secondary">{total} total</Badge>
      </div>

      <Card className="bg-white shadow-sm border-0 overflow-hidden">
        {loading ? (
          <SkeletonRows />
        ) : trips.length === 0 ? (
          <EmptyState text="No trips found" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {[
                      "Trip ID",
                      "Status",
                      "Passenger",
                      "Driver",
                      "Est. Price",
                      "Final",
                      "Date",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {trips.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-gray-50/70 transition-colors cursor-pointer group"
                      onClick={() => onView(t.id)}
                    >
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">
                        {t.id.slice(0, 8)}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {t.passenger?.full_name ?? "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {t.driver?.full_name ?? "—"}
                      </td>
                      <td className="py-3 px-4 font-medium">
                        ₵{Number(t.estimated_price).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        {t.final_price
                          ? `₵${Number(t.final_price).toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-xs">
                        {fmtDate(t.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <Eye className="w-3.5 h-3.5 text-gray-300 group-hover:text-yenko-blue transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50">
                <p className="text-xs text-gray-500">
                  Page {page} of {totalPages} ({total} trips)
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="h-7 w-7 p-0"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="h-7 w-7 p-0"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// ─── Verifications Tab ────────────────────────────────────────────────────────

function VerificationsTab({
  verifications,
  loading,
  searchQuery,
  onSearch,
  onReview,
}: {
  verifications: Verification[];
  loading: boolean;
  searchQuery: string;
  onSearch: (q: string) => void;
  onReview: (
    id: string,
    status: "approved" | "rejected",
    notes?: string,
  ) => void;
}) {
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const pending = verifications.filter((v) => v.status === "pending");
  const reviewed = verifications.filter((v) => v.status !== "pending");

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search verifications…"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Badge variant="secondary">{pending.length} pending</Badge>
      </div>

      {loading ? (
        <SkeletonRows />
      ) : verifications.length === 0 ? (
        <EmptyState text="No verification requests" />
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Pending Review ({pending.length})
              </h3>
              {pending.map((v) => (
                <Card
                  key={v.id}
                  className="p-4 bg-white shadow-sm border-0 border-l-4 border-yellow-400"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium">
                        {v.profiles?.full_name ?? "Unknown User"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {v.profiles?.phone} • {v.profiles?.role} •{" "}
                        {fmtDate(v.created_at)}
                      </p>
                      {v.id_type && (
                        <p className="text-xs text-gray-500 mt-1">
                          ID Type:{" "}
                          <span className="font-medium capitalize">
                            {v.id_type.replace(/_/g, " ")}
                          </span>
                          {v.id_number && (
                            <span className="ml-2 font-mono">
                              {v.id_number}
                            </span>
                          )}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {v.front_image_url && (
                          <a
                            href={v.front_image_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-yenko-blue underline"
                          >
                            Front ↗
                          </a>
                        )}
                        {v.back_image_url && (
                          <a
                            href={v.back_image_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-yenko-blue underline"
                          >
                            Back ↗
                          </a>
                        )}
                        {v.selfie_url && (
                          <a
                            href={v.selfie_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-yenko-blue underline"
                          >
                            Selfie ↗
                          </a>
                        )}
                        {!v.front_image_url &&
                          !v.back_image_url &&
                          !v.selfie_url && (
                            <span className="text-xs text-gray-400 italic">
                              No documents uploaded
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => onReview(v.id, "approved")}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRejectId(v.id);
                          setRejectNotes("");
                        }}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {reviewed.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Reviewed ({reviewed.length})
              </h3>
              <Card className="bg-white shadow-sm border-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {["User", "Role", "Status", "Notes", "Date"].map((h) => (
                        <th
                          key={h}
                          className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {reviewed.map((v) => (
                      <tr key={v.id} className="hover:bg-gray-50/50">
                        <td className="py-3 px-4 font-medium">
                          {v.profiles?.full_name ?? "—"}
                          <p className="text-xs text-gray-400">
                            {v.profiles?.phone}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={v.profiles?.role ?? "—"} />
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={v.status} />
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-xs max-w-xs truncate">
                          {v.admin_notes || "—"}
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-xs">
                          {fmtDate(v.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}
        </>
      )}

      <Dialog open={!!rejectId} onOpenChange={() => setRejectId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Verification</DialogTitle>
            <DialogDescription>
              Optionally add notes explaining the rejection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Reason for rejection (optional)…"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setRejectId(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (rejectId) {
                    onReview(rejectId, "rejected", rejectNotes || undefined);
                    setRejectId(null);
                  }
                }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Waitlist Tab ─────────────────────────────────────────────────────────────

function WaitlistTab({
  waitlist,
  loading,
  searchQuery,
  onSearch,
  onExport,
}: {
  waitlist: WaitlistEntry[];
  loading: boolean;
  searchQuery: string;
  onSearch: (q: string) => void;
  onExport: () => void;
}) {
  const driverCount = waitlist.filter((w) => w.role === "driver").length;
  const passengerCount = waitlist.filter((w) => w.role === "passenger").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, phone, area…"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
        <Badge variant="secondary">{waitlist.length} total</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-sm">
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-blue-400">
          <p className="text-xs text-gray-400 uppercase">Passengers</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {passengerCount}
          </p>
        </Card>
        <Card className="p-4 bg-white shadow-sm border-0 border-l-4 border-yellow-400">
          <p className="text-xs text-gray-400 uppercase">Drivers</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {driverCount}
          </p>
        </Card>
      </div>

      <Card className="bg-white shadow-sm border-0 overflow-hidden">
        {loading ? (
          <SkeletonRows />
        ) : waitlist.length === 0 ? (
          <EmptyState text="No waitlist entries found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {["Name", "Phone", "Email", "Area", "Role", "Joined"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {waitlist.map((w) => (
                  <tr
                    key={w.id}
                    className="hover:bg-gray-50/70 transition-colors"
                  >
                    <td className="py-3 px-4 font-medium">{w.name}</td>
                    <td className="py-3 px-4 text-gray-500">{w.phone}</td>
                    <td className="py-3 px-4 text-gray-400">
                      {w.email || "—"}
                    </td>
                    <td className="py-3 px-4">{w.area}</td>
                    <td className="py-3 px-4">
                      <StatusBadge status={w.role} />
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-xs">
                      {fmtDate(w.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Activity Tab ─────────────────────────────────────────────────────────────

function ActivityTab({
  activity,
  loading,
}: {
  activity: RecentActivity | null;
  loading: boolean;
}) {
  if (loading) return <SkeletonRows rows={8} />;
  if (!activity) return <EmptyState text="No activity data" />;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" />
          Recent Rides ({activity.recentRides.length})
        </h3>
        {activity.recentRides.length === 0 ? (
          <EmptyState text="No recent rides" />
        ) : (
          activity.recentRides.map((ride) => (
            <Card
              key={ride.id}
              className="p-4 bg-white shadow-sm border-0 flex items-center gap-3"
            >
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  ride.status === "completed"
                    ? "bg-green-500"
                    : ride.status === "cancelled"
                      ? "bg-red-400"
                      : "bg-yellow-400"
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {ride.passenger?.full_name ?? "Passenger"} →{" "}
                  {ride.driver?.full_name ?? "Driver"}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {fmtDate(ride.created_at)} • {fmtTime(ride.created_at)}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <StatusBadge status={ride.status} />
                {ride.final_price && (
                  <p className="text-xs font-medium text-gray-700 mt-1">
                    ₵{Number(ride.final_price).toFixed(2)}
                  </p>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
          <Users className="w-3.5 h-3.5" />
          New Users ({activity.recentUsers.length})
        </h3>
        {activity.recentUsers.length === 0 ? (
          <EmptyState text="No recent signups" />
        ) : (
          activity.recentUsers.map((u, i) => (
            <Card
              key={i}
              className="p-4 bg-white shadow-sm border-0 flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-yenko-blue/10 flex items-center justify-center flex-shrink-0">
                <span className="text-yenko-blue text-xs font-bold">
                  {u.full_name?.charAt(0)?.toUpperCase() ?? "?"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{u.full_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {fmtDate(u.created_at)}
                </p>
              </div>
              <StatusBadge status={u.role} />
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
